import Cookies from 'js-cookie';
import {
  TokenPayload,
  PatientLoginResponse,
  MeProviderDataFragment,
  ProviderUserRole,
} from '../generated/graphql';
import { Environment } from '../v2/types/config';

const TOKEN_PAYLOAD_KEY = 'hctp';
const PATIENT_TOKEN_PAYLOAD_KEY = 'hctpc';
const AUTH0_ACCESS_TOKEN_KEY = 'hctpa0';

const storePayload = (
  payload: Record<string, string | boolean | number>,
  key: string,
  expires: number,
) => {
  Cookies.set(key, JSON.stringify(payload), {
    // Secure cookies don't work on localhost with Safari, so use non-secure cookies for development.
    secure: process.env.NODE_ENV !== Environment.Development,
    sameSite: 'lax',
    // Backup expiry. Back-end should take priority and expire on an equal
    // or more aggressive timeline.
    expires, // Days
  });
};

function getPayload<T>(key: string): T | null {
  const item = Cookies.get(key);
  if (!item) {
    return null;
  }
  try {
    return JSON.parse(item);
  } catch (err) {
    // Cookie may be corrupted/truncated (encoding issues, size limits).
    // Remove it and treat as logged-out so the app falls back to the
    // normal login flow instead of crashing.
    console.warn(`[auth] Failed to parse cookie "${key}":`, err);
    Cookies.remove(key);
    return null;
  }
}

export const storeTokenPayload = (tokenPayload: TokenPayload): void => {
  storePayload(tokenPayload, TOKEN_PAYLOAD_KEY, 7);
};

export const storePatientTokenPayload = (
  tokenPayload: PatientLoginResponse,
  expiresInDays: number,
): void => {
  storePayload(tokenPayload, PATIENT_TOKEN_PAYLOAD_KEY, expiresInDays);
};

export const getTokenPayload = (): TokenPayload | null => {
  return getPayload<TokenPayload>(TOKEN_PAYLOAD_KEY);
};

export const getPatientTokenPayload = (): PatientLoginResponse | null => {
  return getPayload<PatientLoginResponse>(PATIENT_TOKEN_PAYLOAD_KEY);
};

export const clearTokenPayload = (): void => {
  Cookies.remove(TOKEN_PAYLOAD_KEY);
};

export const clearPatientTokenPayload = (): void => {
  Cookies.remove(PATIENT_TOKEN_PAYLOAD_KEY);
};

export const storeAuth0AccessToken = (accessToken: string): void => {
  // Only persist the access token. Do not store a V1/V2 mode flag here —
  // routing uses meProvider.isV2 after load, and a sticky isV1 cookie caused
  // blank-screen redirect loops after V1 UI was removed.
  storePayload({ accessToken }, AUTH0_ACCESS_TOKEN_KEY, 7);
};

export const getAuth0AccessToken = (): string | null => {
  return getPayload<{ accessToken: string }>(AUTH0_ACCESS_TOKEN_KEY)
    ?.accessToken;
};

export const clearAuth0AccessToken = (): void => {
  Cookies.remove(AUTH0_ACCESS_TOKEN_KEY);
};

export const isProviderUserOwnerOrAdminAuthorized = (
  providerUser: MeProviderDataFragment | null,
) => {
  return [ProviderUserRole.Owner, ProviderUserRole.Administrator].includes(
    providerUser?.role,
  );
};
